import React, { useMemo } from "react";
import {
  getWatermarkFontStack,
  measureWatermarkText,
  watermarkFontSizePx,
  watermarkFontCssStyle,
  watermarkLogoDisplaySize,
  watermarkTilePositions,
  WATERMARK_CONTENT_TYPES,
} from "./watermarkSettings";
import "./WatermarkThumbMarks.css";

/**
 * Tiny watermark / page-number marks for left-panel Preview thumbs.
 */
const WatermarkThumbMarks = ({
  watermark = null,
  width = 0,
  height = 0,
  /** When true, marks can be clicked (e.g. select another Sign PDF placement). */
  selectable = false,
  onSelect = null,
}) => {
  const w = Math.max(0, Number(width) || 0);
  const h = Math.max(0, Number(height) || 0);
  const isLogoMode =
    watermark?.contentType === WATERMARK_CONTENT_TYPES.LOGO;
  const logoUrl = watermark?.logoUrl || null;
  const hasLogo = Boolean(logoUrl);
  const rotation = Number(watermark?.rotation) || 0;
  const offsetX = Number(watermark?.offsetX) || 0;
  const offsetY = Number(watermark?.offsetY) || 0;
  const fontFamily = getWatermarkFontStack(
    watermark?.fontId || watermark?.fontFamily
  );
  const fontSizePx = watermarkFontSizePx(watermark?.size, w || 140, h || 160);

  const logoSize = useMemo(() => {
    if (!isLogoMode || !hasLogo || !w || !h) {
      return { width: 0, height: 0 };
    }
    return watermarkLogoDisplaySize(
      watermark?.size,
      w,
      h,
      watermark?.logoNaturalWidth,
      watermark?.logoNaturalHeight,
      watermark?.logoCropRect
    );
  }, [isLogoMode, hasLogo, watermark, w, h]);

  const textMetrics = useMemo(
    () =>
      measureWatermarkText(
        watermark?.text?.trim() ? watermark.text : "Your Text",
        fontSizePx,
        fontFamily,
        watermark?.fontWeight,
        {
          letterSpacing: watermark?.letterSpacing,
          textScript: watermark?.textScript,
        }
      ),
    [
      watermark?.text,
      fontSizePx,
      fontFamily,
      watermark?.fontWeight,
      watermark?.letterSpacing,
      watermark?.textScript,
    ]
  );

  const markMetrics = isLogoMode
    ? { width: logoSize.width, height: logoSize.height }
    : textMetrics;

  const hasContent = isLogoMode ? hasLogo : Boolean(watermark?.text?.trim());

  const marks = useMemo(() => {
    if (!hasContent || !w || !h) return [];
    return watermarkTilePositions({
      tile: watermark?.tile || "single",
      spacing: watermark?.spacing,
      stageWidth: w,
      stageHeight: h,
      textWidth: markMetrics.width,
      textHeight: markMetrics.height,
      fontSizePx: isLogoMode
        ? Math.max(markMetrics.width, markMetrics.height)
        : fontSizePx,
      rotationDeg: rotation,
      position: watermark?.position || "center",
      margin: watermark?.margin,
      offsetX,
      offsetY,
      flushEdges: watermark?.flushEdges !== false,
    });
  }, [
    hasContent,
    w,
    h,
    watermark,
    markMetrics,
    fontSizePx,
    rotation,
    isLogoMode,
    offsetX,
    offsetY,
  ]);

  if (!watermark || !hasContent || !marks.length || !w || !h) return null;

  const opacity =
    Math.max(0, Math.min(100, Number(watermark?.opacity) || 0)) / 100;
  const displayText = watermark?.text?.trim() ? watermark.text : "Your Text";

  const handleSelect = (e) => {
    if (!selectable || !onSelect) return;
    e.preventDefault();
    e.stopPropagation();
    onSelect(watermark);
  };

  return (
    <div
      className={`watermark-thumb-marks${selectable ? " is-selectable" : ""}`}
      aria-hidden={selectable ? undefined : true}
    >
      {marks.map((pos, i) => {
        const shared = {
          left: `${pos.x * 100}%`,
          top: `${pos.y * 100}%`,
          opacity,
          transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
        };
        if (isLogoMode) {
          return selectable ? (
            <button
              key={`wtm-${i}`}
              type="button"
              className="watermark-thumb-marks__hit watermark-thumb-marks__hit--logo"
              style={{
                ...shared,
                width: markMetrics.width,
                height: markMetrics.height,
              }}
              aria-label="Select mark"
              onPointerDown={handleSelect}
            >
              <img src={logoUrl} alt="" draggable={false} />
            </button>
          ) : (
            <img
              key={`wtm-${i}`}
              src={logoUrl}
              alt=""
              className="watermark-thumb-marks__logo"
              style={{
                ...shared,
                width: markMetrics.width,
                height: markMetrics.height,
              }}
              draggable={false}
            />
          );
        }
        return selectable ? (
          <button
            key={`wtm-${i}`}
            type="button"
            className="watermark-thumb-marks__hit watermark-thumb-marks__hit--text"
            style={{
              ...shared,
              color: watermark?.color || "#FFFFFF",
              fontFamily,
              fontSize: fontSizePx,
              fontWeight: watermark?.fontWeight === "bold" ? 700 : 400,
              fontStyle: watermarkFontCssStyle(watermark?.fontStyle),
            }}
            aria-label="Select mark"
            onPointerDown={handleSelect}
          >
            {displayText}
          </button>
        ) : (
          <span
            key={`wtm-${i}`}
            className="watermark-thumb-marks__text"
            style={{
              ...shared,
              color: watermark?.color || "#FFFFFF",
              fontFamily,
              fontSize: fontSizePx,
              fontWeight: watermark?.fontWeight === "bold" ? 700 : 400,
              fontStyle: watermarkFontCssStyle(watermark?.fontStyle),
            }}
          >
            {displayText}
          </span>
        );
      })}
    </div>
  );
};

export default WatermarkThumbMarks;
